/**
 * The central control, as the screens read it.
 *
 * The server sends each session its institution's package with the
 * capabilities (`control`: { plan, controls: { id: { state, limits, policy,
 * expiresAt } } }) and pushes a "re-read" the moment the Super Admin changes
 * anything. This module answers the questions a screen asks of that answer —
 * is this module in the menu, may this button create something, what does the
 * banner say — so no screen interprets a state on its own.
 *
 * **A rendering aid, not the boundary.** Every answer here is enforced again
 * by the server on the request (backend/middleware/serviceGate.js). A session
 * with no institution — a patient, an independent doctor, the Super Admin at
 * the platform level — has no package and everything reads as open, which is
 * what the server does too.
 *
 * Pure functions only; Tests/suites/frontend/service-control-client.test.mjs.
 */

export const STATE_LABELS = Object.freeze({
  open: 'مفتوح كامل',
  limited: 'مفتوح بحد',
  trial: 'مؤقت',
  readonly: 'قراءة فقط',
  closed: 'مقفول',
  draining: 'بيتقفل',
});

/** Colour per state, from the 3D tile tones (styles: .tone-*). */
export const STATE_TONES = Object.freeze({
  open: 'emerald',
  limited: 'cyan',
  trial: 'purple',
  readonly: 'amber',
  closed: 'slate',
  draining: 'orange',
});

export const STATE_ICONS = Object.freeze({
  open: 'check-circle-2',
  limited: 'gauge',
  trial: 'hourglass',
  readonly: 'eye',
  closed: 'lock',
  draining: 'log-out',
});

/** The state of one control; no package (or no entry) is open. */
export function stateOf(control, id) {
  const entry = control && control.controls ? control.controls[id] : null;
  return entry && entry.state ? entry.state : 'open';
}

/** In the menu at all? Everything but «مقفول». */
export const isShown = (control, id) => stateOf(control, id) !== 'closed';

/** May a screen offer to create something new under it? */
export const canCreate = (control, id) => ['open', 'limited', 'trial'].includes(stateOf(control, id));

/** May a screen offer to change what exists? Read-only may not; draining may. */
export const canChange = (control, id) => !['readonly', 'closed'].includes(stateOf(control, id));

/** A policy's values (null when the session has no package). */
export function policyOf(control, id) {
  const entry = control && control.controls ? control.controls[id] : null;
  return (entry && entry.policy) || null;
}

/** One limit as { limit, used, warn, percent } — or null for none. */
export function limitOf(control, id, metric) {
  const entry = control && control.controls ? control.controls[id] : null;
  const row = entry && entry.limits ? entry.limits[metric] : null;
  if (!row) return null;
  const percent = row.limit > 0 ? Math.min(100, Math.round((row.used / row.limit) * 100)) : 100;
  return { ...row, percent };
}

/**
 * The desk each screen belongs to, by path. A closed desk leaves the menu;
 * a read-only one stays with a «قراءة فقط» mark and a banner on the screen.
 * The longest matching prefix wins (/hospital/operations is the clinics desk).
 */
export const PATH_CONTROL = Object.freeze({
  '/reception': 'reception',
  '/operations': 'clinicsDesk',
  '/hospital/operations': 'clinicsDesk',
  '/secretary': 'secretaryDesk',
  '/contracts': 'contracts',
  '/accounts': 'accountsDesk',
  '/rooms': 'rooms',
  '/nursing': 'nursing',
  '/lab': 'lab',
  '/radiology': 'radiology',
  '/pharmacy': 'pharmacy',
  '/doctor/offers': 'offers',
  '/doctor/vouchers': 'vouchers',
  '/doctor/packages': 'marketingPackages',
  '/doctor/photography': 'photography',
});

export function controlForPath(pathname) {
  const path = String(pathname || '');
  let best = null;
  for (const prefix of Object.keys(PATH_CONTROL)) {
    if ((path === prefix || path.startsWith(`${prefix}/`)) && (!best || prefix.length > best.length)) best = prefix;
  }
  if (!best) return null;
  // A screen's own settings page is the account's, not the desk's.
  if (path.endsWith('/settings')) return null;
  return PATH_CONTROL[best];
}

/** Hide menu rows whose desk the package has closed. */
export function visibleNavItems(items, control) {
  if (!control) return items;
  const kept = items.filter((item) => {
    if (item.section) return true;
    const id = controlForPath(item.to);
    return !id || isShown(control, id);
  });
  // Drop a section heading left with nothing under it.
  return kept.filter((item, i) => !item.section || (kept[i + 1] && !kept[i + 1].section));
}

const dayCount = (iso, now) => Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / 86400000));

/**
 * What the banner on top of a screen says, if anything:
 * { tone, icon, title, text } or null.
 */
export function bannerFor(control, id, now = new Date()) {
  if (!control || !id) return null;
  const state = stateOf(control, id);
  const entry = control.controls[id] || {};
  if (state === 'readonly') {
    return {
      tone: 'amber',
      icon: 'eye',
      title: 'الوحدة دي «قراءة فقط» في باقة المنشأة',
      text: 'تقدر تشوف وتطبع كل القديم، ومن غير إضافة أو تعديل جديد. لو محتاجين ترجع، كلّموا إدارة المنصة.',
    };
  }
  if (state === 'draining') {
    return {
      tone: 'orange',
      icon: 'log-out',
      title: 'الوحدة دي بتتقفل: ما فيش جديد',
      text: 'كمّلوا الطلبات المفتوحة لحد ما تخلص، والجديد مش هيتقبل. القفل بيكتمل لوحده لما المفتوح يخلص.',
    };
  }
  if (state === 'trial' && entry.expiresAt) {
    const days = dayCount(entry.expiresAt, now);
    return {
      tone: 'purple',
      icon: 'hourglass',
      title: days <= 1 ? 'تجربة الوحدة دي بتخلص النهارده' : `تجربة الوحدة دي باقي عليها ${days} يوم`,
      text: 'الوحدة مفتوحة كتجربة، وبترجع لوحدها لحالة الباقة في آخر يوم. كلّموا إدارة المنصة لو عايزين تكمّلوها.',
    };
  }
  return null;
}

/** Every limit at 80% or more, for the package card and the warnings. */
export function warnings(control, labels = {}) {
  if (!control || !control.controls) return [];
  const out = [];
  for (const [id, entry] of Object.entries(control.controls)) {
    for (const [metric, row] of Object.entries(entry.limits || {})) {
      if (row.warn) out.push({ id, metric, label: labels[`${id}.${metric}`] || labels[id] || id, ...row });
    }
  }
  return out;
}

/** «استهلكتم 85 من 100 …» — the toast a request near its limit raises. */
export function limitToast(header, labels = {}) {
  const match = /^([\w]+)\.([\w]+);(\d+);(\d+)$/.exec(String(header || ''));
  if (!match) return null;
  const [, id, metric, used, limit] = match;
  const label = labels[`${id}.${metric}`] || labels[id] || 'الخدمة';
  return `استهلكتم ${used} من ${limit} في «${label}» حسب باقة المنشأة.`;
}

/**
 * The department a request goes to → its control. Mirrors
 * Backend/lib/serviceCatalog.js DEPARTMENT_CONTROL (a parity test holds them
 * together): «طلب خدمة» offers only the departments the package carries.
 */
export const DEPARTMENT_CONTROL = Object.freeze({
  checkup: 'clinicsDesk',
  lab: 'lab',
  radiology: 'radiology',
  pharmacy: 'pharmacy',
  medication: 'pharmacy',
  icu: 'rooms',
  ward: 'rooms',
  surgery: 'rooms',
});

/**
 * Capabilities that exist only to work one module — the ones a closed module
 * takes away on the server (Backend/lib/serviceCatalog.js PERMISSION_CONTROL).
 * The administrative manager's privilege form greys them out with the reason.
 */
export const PERMISSION_CONTROL = Object.freeze({
  LAB_WORK: 'lab',
  RADIOLOGY_WORK: 'radiology',
  PHARMACY_DISPENSE: 'pharmacy',
  NURSING_CARE: 'nursing',
  BILLING_MANAGE: 'accountsDesk',
  SERVICE_REQUEST_APPROVE: 'contracts',
  RECEIPT_ISSUE: 'receipts',
  SCHEDULE_SHIFT: 'scheduleDelay',
  QUEUE_ADVANCE: 'queue',
  QUEUE_OVERSEE: 'queue',
  SHARE_LINK_ISSUE: 'portal',
  SHARE_LINK_REVOKE: 'portal',
  AI_ANALYST: 'aiAnalysis',
  AI_PREMIUM_MODEL: 'aiPremium',
  INTAKE_FIELDS_MANAGE: 'reception',
});

/**
 * The desk each staff role works — closing the desk closes the role to new
 * accounts. Mirrors Backend/lib/serviceCatalog.js DESK_CONTROL.
 */
export const DESK_CONTROL = Object.freeze({
  receptionist: 'reception',
  operations_manager: 'clinicsDesk',
  secretary: 'secretaryDesk',
  contracts_manager: 'contracts',
  accounts_manager: 'accountsDesk',
  rooms_manager: 'rooms',
  nurse: 'nursing',
  lab_technician: 'lab',
  radiologist: 'radiology',
  pharmacist: 'pharmacy',
});

/** May the hospital create an account of this role under its package? */
export function roleOffered(control, role) {
  if (!control) return true;
  if (role === 'staff' || role === 'manager') return true;
  if (Array.isArray(control.roles) && !control.roles.includes(role)) return false;
  const desk = DESK_CONTROL[role];
  return !desk || canCreate(control, desk);
}

/** Arabic names for the metrics the screens show, keyed control.metric. */
export const METRIC_LABELS = Object.freeze({
  'helpAssistant.perDay': 'أسئلة المساعد الذكي اليوم',
  'helpAssistant.perUserDay': 'أسئلتك للمساعد الذكي اليوم',
  'supportTickets.perMonth': 'تذاكر الدعم الشهر ده',
  'staffAccounts.total': 'الحسابات',
  'catalogLimits.clinics': 'العيادات',
  'catalogLimits.services': 'الخدمات',
  'intakeFields.count': 'حقول الاستقبال',
  'attachments.storageGb': 'مساحة المرفقات (جيجا)',
  'portalLinks.perMonth': 'روابط المرضى الشهر ده',
  'aiAnalysis.perDay': 'التحليلات الذكية اليوم',
  'aiAnalysis.perDoctorDay': 'تحليلاتك الذكية اليوم',
  'aiPremium.perMonth': 'تحليلات «الأذكى» الشهر ده',
  'ocr.pagesPerMonth': 'صفحات قراءة الصور الشهر ده',
  'hallScreens.count': 'شاشات القاعة',
  'contractParties.count': 'جهات التعاقد',
  'roomsCapacity.rooms': 'الغرف',
  'roomsCapacity.beds': 'الأسرّة',
  'photography.perMonth': 'جلسات التصوير الشهر ده',
});

/** One limit as the board names it: «صفحات في الشهر: 4000 صفحة», not its key. */
function limitText(spec, key, value) {
  if (value === null) return `${spec ? spec.label : key}: غير محدود`;
  return spec ? `${spec.label}: ${value} ${spec.unit || ''}`.trim() : `${key}=${value}`;
}

/** A policy field's value as the editor shows it. */
function policyText(field, value) {
  const named = (v) => (field.options || []).find(([key]) => String(key) === String(v))?.[1] || v;
  if (field.kind === 'multi') return (value || []).length ? value.map(named).join(' و') : 'ولا حاجة';
  if (field.kind === 'number') return value === null ? 'من غير حد' : `${value} ${field.unit || ''}`.trim();
  return named(value);
}

/**
 * What a change-log row changed, in one line the board prints: limits and
 * policies by their names and values, never their keys. `controlOf` finds a
 * control's catalog entry.
 */
export function auditSummary(row, labelOf, controlOf) {
  const after = row.after || {};
  const before = row.before || {};
  const parts = [];
  const specs = (controlOf && controlOf(row.controlId)?.limits) || [];
  // The server names the packages; an older payload still has the keys only.
  if (row.action === 'plan') return `من «${before.planName || before.planKey || '—'}» إلى «${after.planName || after.planKey || '—'}»`;
  if (row.action === 'plan-edit') return 'تعديل على قالب الباقة';
  if (after.state || before.state) parts.push(`${STATE_LABELS[before.state] || 'الباقة'} ← ${STATE_LABELS[after.state] || 'الباقة'}`);
  if (after.limits) parts.push(Object.entries(after.limits).map(([m, v]) => limitText(specs.find((l) => l.metric === m), m, v)).join('، '));
  if (after.policy) {
    const fields = (controlOf && controlOf(row.controlId)?.fields) || [];
    const said = fields.filter((f) => after.policy[f.name] !== undefined).map((f) => `${f.label}: ${policyText(f, after.policy[f.name])}`);
    parts.push(said.length ? said.join('، ') : 'سياسة جديدة');
  }
  if (after.columns) parts.push(`حدود العقد: ${Object.entries(after.columns).map(([m, v]) => (typeof v === 'object' && v !== null ? `${m}=…` : limitText(specs.find((l) => l.column === m), m, v))).join('، ')}`);
  if (after.expiresAt) parts.push(`لحد ${new Date(after.expiresAt).toLocaleDateString('ar-EG')}`);
  if (after.draining) parts.push('بيتقفل تدريجيًا');
  return parts.join(' · ') || (labelOf ? labelOf(row.controlId) : '');
}
